import React, { useState, useRef, useEffect } from "react";
import api from "../api";
import { Bot, Send, Sparkles } from "lucide-react";

/**
 * AI Security Agent — report-aware chat (Module 4). Grounded in one scan: the
 * backend injects that scan's findings/remediation/compliance/asset data (+ the
 * previous scan for trend) so answers are specific and non-hallucinated.
 */
const SUGGESTIONS = [
  "What should I fix first?",
  "Explain my biggest risk in plain English",
  "How does this compare to my last scan?",
  "Give me a prioritized remediation plan",
];

export default function ScanAgentChat({ scanId }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading || !scanId) return;
    setErr("");
    const next = [...messages, { role: "user", content: q }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const r = await api.post(`/api/assistant/scan/${scanId}`, { messages: next });
      setMessages([...next, { role: "assistant", content: r.data?.reply || "…" }]);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Agent unavailable — please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="au-glass au-pad" style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 11, color: "var(--au-accent-ink)", letterSpacing: 2, marginBottom: 10, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
        <Sparkles size={14} /> ASK THE AI SECURITY AGENT
      </div>
      <div style={{ fontSize: 12.5, color: "var(--au-muted)", marginBottom: 14, lineHeight: 1.6 }}>
        Grounded in this scan — ask it to prioritize, explain a finding, give fix steps, or compare to your last scan.
      </div>

      {messages.length === 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
          {SUGGESTIONS.map((s) => (
            <button key={s} onClick={() => send(s)} disabled={loading}
              className="au-pill au-pill-ghost au-pill-sm" style={{ fontSize: 12 }}>{s}</button>
          ))}
        </div>
      )}

      {messages.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14, maxHeight: 440, overflowY: "auto" }}>
          {messages.map((m, i) => (
            <div key={i} style={{ alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: "88%" }}>
              <div style={{
                fontSize: 13.5, lineHeight: 1.65, whiteSpace: "pre-wrap",
                padding: "10px 14px", borderRadius: 12,
                background: m.role === "user" ? "var(--au-glass-strong)" : "var(--au-glass)",
                border: `1px solid ${m.role === "user" ? "var(--au-accent-ink)" : "var(--au-hair)"}`,
                color: "var(--au-text)",
              }}>{m.content}</div>
            </div>
          ))}
          {loading && (
            <div style={{ alignSelf: "flex-start", fontSize: 13, color: "var(--au-muted)", display: "flex", alignItems: "center", gap: 8, padding: "6px 4px" }}>
              <Bot size={14} /> Analyzing your scan…
            </div>
          )}
          <div ref={endRef} />
        </div>
      )}

      {err && <div style={{ fontSize: 12, color: "var(--au-error)", marginBottom: 10 }}>{err}</div>}

      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          className="au-input"
          style={{ flex: 1 }}
          placeholder="Ask about this report…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          disabled={loading}
        />
        <button className="au-pill au-pill-primary" onClick={() => send()} disabled={loading || !input.trim()}>
          <Send size={15} /> Ask
        </button>
      </div>
    </div>
  );
}
